/**
 * Application frame: left navigation, sticky status strip, routed content.
 *
 * The navigation is role-aware, but that is an affordance only — the server
 * re-derives scope on every request, so hiding a link is never the control that
 * keeps a clinic user out of the JMC screens.
 */
import { applyTheme } from '../theme-apply.ts';
import { ROLE_LABEL, sessionRole } from '../roles.ts';
import { AppDialogHost } from './AppDialog.tsx';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import {
  NavLink,
  Outlet,
  ScrollRestoration,
  useLocation,
  useNavigation,
  useRevalidator,
  useRouteLoaderData,
} from 'react-router';
import { imageUrl, post, type Session } from '../api.ts';
import { clearCache } from '../cache.ts';
import { Petals } from './Hero.tsx';
import {
  AnalyticsIcon,
  ArticleIcon,
  ArticleNewIcon,
  AuditIcon,
  ClinicIcon,
  CtaIcon,
  DashboardIcon,
  LegalIcon,
  LogoutIcon,
  PersonaIcon,
  PromptIcon,
  SettingsIcon,
  StrengthIcon,
  StoreIcon,
  SupervisorIcon,
  TreatmentIcon,
  UserIcon,
} from './icons.tsx';
import { m, SPRING_SLIDE, useBlockGlide } from './motion.tsx';
import { ScopeSwitchProvider, useDelayedFlag } from './Skeleton.tsx';
import { StepUpDialog } from './StepUpDialog.tsx';
import { Select } from './Select.tsx';

/**
 * The two role contexts. A person is in exactly one of them.
 *
 * A JMC operator administers the platform and holds no clinic membership; a
 * clinic admin or store user works inside one clinic. Migration 011 enforces
 * that separation in the database, so this nav never has to offer both - which
 * is what it used to do, and what made the mistake visible.
 *
 * Every item carries its own icon. A nav where three items share one is worse
 * than a nav with none - the icon stops being a landmark and becomes decoration.
 */
const CLINIC_NAV = [
  { to: '/', label: 'ダッシュボード', end: true, icon: <DashboardIcon /> },
  { to: '/articles', label: '記事一覧', icon: <ArticleIcon /> },
  { to: '/articles/new', label: '新規記事作成', icon: <ArticleNewIcon /> },
  /*
   * The strengths screen carries five fact categories - strengths, treatment
   * policy, counselling policy, equipment, and その他 for material none of those
   * describes - which is where an article's framing comes from, and it was
   * reachable only by knowing the URL.
   */
  { to: '/catalog/strengths', label: 'クリニックの強み', icon: <StrengthIcon /> },
  { to: '/personas', label: 'ペルソナ管理', icon: <PersonaIcon /> },
  { to: '/catalog/treatments', label: '施術・料金', icon: <TreatmentIcon /> },
  { to: '/catalog/supervisors', label: '監修医', icon: <SupervisorIcon /> },
  { to: '/catalog/cta-templates', label: 'CTAテンプレート', icon: <CtaIcon /> },
];

/*
 * Clinic-admin only.
 *
 * Every route here is guarded server-side by requireClinicAdmin, so a store user
 * who followed the link got a 403 screen - a nav that offers work the person
 * cannot do reads as a broken application rather than as a permission boundary.
 */
interface NavItem {
  to: string;
  label: string;
  icon: React.ReactElement;
  /** Only the dashboard matches exactly; every other link matches its subtree. */
  end?: boolean;
}

const CLINIC_ADMIN_NAV: NavItem[] = [
  { to: '/stores', label: 'クリニック・店舗管理', icon: <ClinicIcon /> },
  { to: '/members', label: 'ユーザー管理', icon: <UserIcon /> },
  { to: '/settings', label: 'クリニック設定', icon: <SettingsIcon /> },
];

/*
 * A store admin: their store(s) and the people in them - not the clinic's
 * settings, which stay the clinic admin's.
 */
const STORE_ADMIN_NAV: NavItem[] = [
  { to: '/stores', label: '店舗管理', icon: <ClinicIcon /> },
  { to: '/members', label: 'ユーザー管理', icon: <UserIcon /> },
];

const JMC_NAV = [
  { to: '/admin', label: 'ダッシュボード', end: true, icon: <DashboardIcon /> },
  { to: '/admin/tenants', label: 'クリニック・店舗管理', icon: <ClinicIcon /> },
  { to: '/admin/users', label: 'ユーザー管理', icon: <UserIcon /> },
  { to: '/admin/prompts', label: 'プロンプト管理', icon: <PromptIcon /> },
  { to: '/admin/defaults', label: '共通マスタ', icon: <StrengthIcon /> },
  { to: '/admin/legal', label: '法令/RAG資料管理', icon: <LegalIcon /> },
  { to: '/admin/rulebook', label: 'チェックルール管理', icon: <AuditIcon /> },
  { to: '/admin/analytics', label: '店舗別ブログ作成分析', icon: <AnalyticsIcon /> },
  { to: '/admin/audit', label: '監査ログ', icon: <AuditIcon /> },
  { to: '/admin/settings', label: 'システム設定', icon: <SettingsIcon /> },
];

export default function AppShell() {
  const session = useRouteLoaderData('root') as Session;
  /*
   * The clinic's theme, on every change of session - sign-in, a store switch,
   * a colour saved in settings (which revalidates the session). A layout
   * effect, so the new colours land before the browser paints the new scope.
   * The JMC workspace always wears the default: the server sends null.
   */
  const brandColor = session.theme?.brandColor ?? null;
  useLayoutEffect(() => applyTheme(brandColor), [brandColor]);
  const location = useLocation();
  const navigation = useNavigation();
  const revalidator = useRevalidator();

  // Two different states, deliberately treated differently:
  //   navigating  — moving to another screen; nothing to show there yet.
  //   revalidating— a background refresh (an SSE change event, a filter);
  //                 the current data is still valid and stays on screen.
  const navigating = navigation.state === 'loading';
  const refreshing = revalidator.state === 'loading' && !navigating;

  // Only after the wait outlasts the threshold, so a fast navigation never
  // flashes a progress bar.
  const showProgress = useDelayedFlag(navigating, 250, 250);

  const clinic = session.memberships.find((m) => m.clinicId === session.activeClinicId);
  const stores = clinic?.stores ?? [];
  /*
   * Per instance, never a string literal: two navigations sharing a hard-coded
   * layoutId would share one marker and fling it between them.
   */
  const pillId = useId();
  const [switching, setSwitching] = useState(false);
  const sawLoading = useRef(false);
  const mainRef = useRef<HTMLElement>(null);
  useBlockGlide(mainRef);
  const isPlatformAdmin = session.user.platformRole === 'JMC_ADMIN';
  /* Clinic admin on any of their memberships; otherwise a store user. */
  const clinicAdmin = session.memberships.some((m) => m.clinicAdmin);
  const role = sessionRole(session).role;
  /*
   * The role decides the workspace, not the current URL. A clinic user who lands
   * on an /admin path gets a 403 from the server, not a nav that pretends they
   * work there.
   */
  const navItems = isPlatformAdmin
    ? JMC_NAV
    : clinicAdmin
      ? [...CLINIC_NAV, ...CLINIC_ADMIN_NAV]
      : role === 'STORE_ADMIN'
        ? [...CLINIC_NAV, ...STORE_ADMIN_NAV]
        : CLINIC_NAV;

  /*
   * Change the working scope without reloading the document.
   *
   * This used to call navigate(0), which is a full browser reload: the shell was
   * torn down and rebuilt, so the sidebar, the status strip and every tile
   * flashed white and the scroll position jumped. A revalidation re-runs the
   * same loaders - including the root one that carries the session - while the
   * shell stays mounted, so only the data changes.
   */
  /*
   * One switch at a time. Two overlapping POSTs is not a hypothetical: picking a
   * clinic and then a store is two clicks a second apart, and both selects
   * stayed live while the first request was still out.
   */
  async function switchScope(clinicId: string, storeId: string) {
    if (switching) return;
    setSwitching(true);
    try {
      await post('/auth/switch-scope', { clinicId, storeId: storeId || undefined });
      // Entries are keyed by scope, so this is belt and braces - but a cache
      // that outlives the scope it was read under is the one bug in a cache
      // that must never happen here.
      clearCache();
      await revalidator.revalidate();
    } catch {
      setSwitching(false);
    }
  }

  /*
   * Held until the refetch actually finishes. revalidate() may resolve before
   * the router has even entered its loading state, so clearing the flag on that
   * promise alone would drop the placeholders a frame after raising them - which
   * is the flicker they exist to prevent. Waiting to observe loading -> idle is
   * what makes it correct.
   */
  useEffect(() => {
    if (!switching) {
      sawLoading.current = false;
      return;
    }
    if (revalidator.state === 'loading') sawLoading.current = true;
    else if (sawLoading.current) setSwitching(false);
  }, [switching, revalidator.state]);

  /**
   * Sign out, and leave nothing of this session in the document.
   *
   * A document load rather than a client-side navigation, for the same reason
   * signing in is one: the event stream opened with this session, the loader
   * cache and every component's state belong to the person who is leaving. A
   * SPA navigation kept the stream attached, and when the server noticed the
   * session was revoked it evicted whoever was using the app by then - which,
   * after signing in as somebody else, was the new account.
   *
   * The request is allowed to fail without stopping the exit: the cookie is
   * cleared server-side on success, and if the call never landed, leaving the
   * document is still the right answer for the person pressing the button. The
   * session's own idle window closes it.
   */
  async function signOut() {
    try {
      await post('/auth/logout');
    } catch {
      // Nothing to report: the person asked to leave, and they are leaving.
    }
    clearCache();
    // The next person at this browser signs in to the product, not to this clinic.
    applyTheme(null);
    window.location.assign('/login');
  }

  return (
    <div className="shell">
      {/*
       * Landing at the bottom of a new page/stage, having to scroll up by hand.
       *
       * React Router does not touch the scroll position on a client-side
       * navigation - only a full document load does that natively - so moving
       * from the plan screen to the write screen, or the write screen to the
       * final one, kept whatever scrollY the PREVIOUS page had ended at
       * (frequently the bottom of a long article). This is the standard fix:
       * it resets to the top on a normal (PUSH) navigation and restores the
       * exact position on back/forward (POP), which is what a browser already
       * does for a document navigation and what a SPA has to opt back into.
       *
       * Keyed on `location.key`, which a real navigation changes and which a
       * scope switch (see ScopeSwitchProvider below) does NOT - that handover
       * deliberately keeps the scroll position, and this does not fight it.
       */}
      <ScrollRestoration />
      {/* The app's confirm / alert / prompt. See AppDialog.tsx. */}
      <AppDialogHost />
      {showProgress && <div className="route-progress" role="presentation" />}
      <a className="skip-link" href="#main">
        本文へスキップ
      </a>

      <nav className="sidebar" aria-label="メインナビゲーション">
        {/* Decoration, gathered in the lower corner away from the text. */}
        <Petals layout="corner" />

        <div className="sidebar__brand">
          {/*
            icon.svg, not logo.svg. At 22px the full mark - a folded document
            around a flower, over two page leaves - is a pink smudge, and it is
            the same 22px square the browser tab shows. Same asset in both, so
            the thing beside the tab title and the thing at the top of the
            sidebar are recognisably one mark.
          */}
          <span className="sidebar__logo">
            <img src="/icon.svg" alt="" width={22} height={22} aria-hidden />
          </span>
          <span className="sidebar__brandtext">
            <span className="sidebar__brandname">記事咲</span>
            <span className="sidebar__brandsub">きじさく</span>
          </span>
        </div>

        <div className="sidebar__links">
          <div className="sidebar__section">
            {isPlatformAdmin ? 'JMC管理者画面' : 'クリニックユーザー画面'}
          </div>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end ?? false}
              className="sidebar__link"
              /*
               * Clicking the link for the screen you are already on is a no-op,
               * not a reload.
               *
               * React Router treats a navigation to the current URL as a
               * navigation, so it re-runs that route's loaders - which is a
               * fresh request for data the screen is already showing. Nothing
               * asked for it: the person clicked where they already were.
               *
               * A query string is the exception. On `/articles?status=DRAFT` the
               * sidebar link points at bare `/articles`, and clicking it to
               * clear the filter is a real navigation to a different URL.
               */
              onClick={(event) => {
                if (location.pathname === item.to && location.search === '') {
                  event.preventDefault();
                }
              }}
            >
              {({ isActive }) => (
                <>
                  {/*
                    One element that moves between rows, rather than a background
                    that blinks out here and reappears there. The shared layoutId
                    is what makes it travel: React unmounts it from the old row
                    and mounts it in the new one, and motion animates between the
                    two measured positions.
                  */}
                  {isActive && (
                    <m.span
                      className="sidebar__active"
                      layoutId={pillId}
                      transition={SPRING_SLIDE}
                    />
                  )}
                  <span className="sidebar__icon">{item.icon}</span>
                  <span className="sidebar__label">{item.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>

        {/*
          Identity and sign-out live here rather than in the status strip. The
          strip is for the working scope, which changes many times an hour; who
          you are does not, and it was competing for the same row.
        */}
        <div className="sidebar__foot">
          {/*
            Their picture when they have one, and the first letter of their name
            when they do not. Never a generic silhouette: the initial is the one
            thing that differs between two accounts, and an outline is the one
            thing that does not.
          */}
          {/*
            The name in the corner is where people look for their own account,
            so it is the link to it rather than a label beside one.
          */}
          <NavLink className="sidebar__me" to="/profile" title="プロフィール">
            <span className="sidebar__avatar" aria-hidden>
              {session.user.avatarId ? (
                /*
                 * One request per picture, ever: the URL is the image's own id
                 * and is served immutable, so this is a cache hit on every
                 * screen after the first.
                 */
                <img src={imageUrl(session.user.avatarId)} alt="" width={30} height={30} />
              ) : (
                (session.user.displayName || session.user.email || '?')
                  .trim()
                  .charAt(0)
                  .toUpperCase()
              )}
            </span>
            <span className="sidebar__who">
              <span className="sidebar__name">
                {session.user.displayName || session.user.email}
              </span>
              <span className="sidebar__role">
                {ROLE_LABEL[isPlatformAdmin ? 'JMC_ADMIN' : clinicAdmin ? 'CLINIC_ADMIN' : (role ?? 'STORE_USER')]}
              </span>
            </span>
          </NavLink>
          <button
            className="sidebar__signout"
            type="button"
            onClick={() => void signOut()}
            title="ログアウト"
          >
            <LogoutIcon />
            <span>ログアウト</span>
          </button>
        </div>
      </nav>

      <div className="main">
        {/*
          The strip exists for the working scope and nothing else, so it is not
          rendered when there is no scope to work with. A JMC operator holds no
          membership: for them this was an empty 40px bar pushing every screen
          down - what an always-rendered container costs once the things that
          used to fill it have moved elsewhere.
        */}
        {/*
          Only for somebody with a scope. It used to appear for a JMC operator
          too WHILE refreshing - a 44px bar that pushed the whole screen down
          and pulled it back up again after every save, which is what made each
          new tile on the JMC clinic page jump. Their 更新中 floats instead
          (below), taking no room.
        */}
        {session.memberships.length > 0 && (
          <div className="statusbar">
            {clinic && (
              <>
                <span className="scope">
                  <span className="scope__icon" aria-hidden>
                    <ClinicIcon size={15} />
                  </span>
                  {/*
                    A control only where there is a choice. One clinic and one
                    store is the store user's whole world - a dropdown listing the
                    single thing they already belong to is a decision they cannot
                    make, dressed up as one they can.
                  */}
                  {session.memberships.length > 1 ? (
                    <>
                      <label className="scope__label" htmlFor="clinic-select">
                        クリニック
                      </label>
                      <Select
                        id="clinic-select"
                        className="scope__select"
                        value={session.activeClinicId ?? ''}
                        disabled={switching}
                        onChange={(clinicId) => void switchScope(clinicId, '')}
                        options={session.memberships.map((m) => ({
                          value: m.clinicId,
                          label: m.clinicName,
                        }))}
                      />
                    </>
                  ) : (
                    <span className="scope__value">{clinic.clinicName}</span>
                  )}
                </span>

                {stores.length > 0 && (
                  <>
                    <span className="scope__sep" aria-hidden>
                      /
                    </span>
                    <span className="scope">
                      <span className="scope__icon" aria-hidden>
                        <StoreIcon size={15} />
                      </span>
                      {stores.length > 1 ? (
                        <>
                          <label className="scope__label" htmlFor="store-select">
                            店舗
                          </label>
                          <Select
                            id="store-select"
                            className="scope__select"
                            value={session.activeStoreId ?? ''}
                            disabled={switching}
                            onChange={(storeId) =>
                              void switchScope(session.activeClinicId!, storeId)
                            }
                            options={[
                              { value: '', label: '全店舗' },
                              ...stores.map((store) => ({ value: store.id, label: store.name })),
                            ]}
                          />
                        </>
                      ) : (
                        // One store: naming it is the whole of the information.
                        <span className="scope__value">{stores[0]!.name}</span>
                      )}
                    </span>
                  </>
                )}
              </>
            )}

            {/* No clinic chosen yet, and more than one to choose from. */}
            {!clinic && session.memberships.length > 0 && (
              <span className="scope">
                <span className="scope__icon" aria-hidden>
                  <ClinicIcon size={15} />
                </span>
                <label className="scope__label" htmlFor="clinic-select">
                  クリニック
                </label>
                <Select
                  id="clinic-select"
                  className="scope__select"
                  value=""
                  disabled={switching}
                  onChange={(clinicId) => void switchScope(clinicId, '')}
                  options={session.memberships.map((m) => ({
                    value: m.clinicId,
                    label: m.clinicName,
                  }))}
                />
              </span>
            )}

            {/* A background refresh says so quietly; it never blanks the page. */}
            {refreshing && (
              <span className="refreshing" role="status" aria-live="polite">
                更新中
              </span>
            )}
          </div>
        )}

        {session.memberships.length === 0 && refreshing && (
          <span className="refreshing refreshing--float" role="status" aria-live="polite">
            更新中
          </span>
        )}

        <main id="main" className="content" ref={mainRef}>
          {/*
            The key remounts the content when the route changes, so the new
            screen simply fades in over its own layout. No AnimatePresence and
            no exit animation: waiting for the old screen to leave empties the
            page for a frame, which is exactly what looks like a flicker.

            Keyed on pathname only, so a query-string change (a tab, a filter)
            updates in place rather than replaying the whole entrance.
          */}
          {/*
            Keyed on the route AND the working scope, so React remounts on both -
            which is what restarts the CSS entrance. `stagger` walks the cards in
            behind it.

            The scope was missing, and that is the whole reason a store switch
            looked abrupt: the pathname does not change, so the subtree was
            reused and the new figures simply replaced the old ones in place with
            no transition at all. Switching store is as much a change of what the
            screen is about as navigating is.

            Remounting also discards the screen's local state - open edit panels,
            expanded rows - which is correct here rather than incidental: that
            state was about the store being switched away from.
          */}
          {/*
            Two elements, and they are two on purpose.

            The OUTER one carries the handover. While a switch is in flight every
            figure below belongs to the store being left, so the page recedes and
            stops accepting input - answered once here rather than screen by
            screen. It recedes rather than blanking: blanking collapses the page
            and takes the scroll position with it, so the new content would arrive
            to a window that had jumped to the top. `pointer-events: none` matters
            as much as the opacity, because a click landing on the previous
            store's row during the handover opens the previous store's record.

            The INNER one carries the identity of what is on screen, and is what
            the key above remounts.

            Separate because the handover suppresses nothing on the child: on one
            element the recede's opacity and the entrance's opacity keyframes
            fight, and fadeInUp would undo the recede it is meant to hand over to.
          */}
          <ScopeSwitchProvider switching={switching}>
            <div
              className={switching ? 'pageshift pageshift--switching' : 'pageshift'}
              // So a screen reader is told the region is being replaced rather
              // than reading a mixture of the old scope's data and the new.
              aria-busy={switching || undefined}
            >
              <div
                key={`${location.pathname}|${session.activeClinicId ?? ''}|${session.activeStoreId ?? ''}`}
                className="page animate-fade-up stagger"
              >
                <Outlet context={session} />
              </div>
            </div>
          </ScopeSwitchProvider>

          {/*
            Mounted once, for the whole app: the few actions that ask somebody to
            prove themselves again are answered here rather than by sending the
            browser away from a half-filled form. See StepUpDialog.
          */}
          <StepUpDialog hasPassword={session.user.hasPassword ?? false} />
        </main>
      </div>
    </div>
  );
}
